/**
 * Public Scan Redux Slice
 *
 * Manages state for public NFC scan page (no authentication required).
 * Uses gateway injection pattern for testability.
 *
 * Story 3.7.c - Redux state management for viewer NFC scan context
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState, StoreExtraDependencies } from '../index';
import type { ViewerNfcScanContextResponse } from '@/domain/types/ViewerNfcScanContext';

/**
 * Public Scan state structure
 */
export interface PublicScanState {
  scanContext: ViewerNfcScanContextResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: PublicScanState = {
  scanContext: null,
  loading: false,
  error: null,
};

/**
 * Async Thunk: Fetch Public Scan Context
 *
 * Fetches NFC scan context for a viewer (authenticated or anonymous).
 * Single API call that returns different context based on item state.
 */
export const fetchPublicScanContext = createAsyncThunk<
  ViewerNfcScanContextResponse,
  { productId: string; itemId: string; signature: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>('publicScan/fetchContext', async ({ productId, itemId, signature }, { extra, rejectWithValue }) => {
  try {
    const { publicScanGateway } = extra;
    return await publicScanGateway.getScanContext(productId, itemId, signature);
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Unknown error');
  }
});

/**
 * Public Scan Slice
 */
const publicScanSlice = createSlice({
  name: 'publicScan',
  initialState,
  reducers: {
    /**
     * Clear scan context (reset state)
     */
    clearScanContext(state) {
      state.scanContext = null;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // Fetch Public Scan Context
    builder
      .addCase(fetchPublicScanContext.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicScanContext.fulfilled, (state, action) => {
        state.scanContext = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPublicScanContext.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load scan context';
        state.scanContext = null;
      });
  },
});

// Actions
export const { clearScanContext } = publicScanSlice.actions;

// Selectors
export const selectScanContext = (state: RootState) => state.publicScan.scanContext;
export const selectScanLoading = (state: RootState) => state.publicScan.loading;
export const selectScanError = (state: RootState) => state.publicScan.error;

// Reducer
export default publicScanSlice.reducer;
